import { useIntl } from "react-intl";
import { Outlet } from "react-router-dom";
import { SecondaryNavbar, SiteContent, type NavbarItem } from "@packsize/components";
import { useAuthorizedValue, useFeatureFlaggedValue, useLicensedValue } from "@packsize/authentication";

import { withErrorBoundary } from "@/components/error-boundary";
import { environment } from "@/environment";
import { useHasNazomiMachine } from "@/hooks/machines/use-has-nazomi-machine";

const ConfigurationNav = withErrorBoundary(() => {
	const intl = useIntl();
	const hasNazomiMachine = useHasNazomiMachine({ enabled: true });
	const ordersAuditTab = useAuthorizedValue(
		{ link: "orders-audit", text: intl.formatMessage({ id: "Orders Audit", defaultMessage: "Orders Audit" }) },
		"accessOrdersAudit",
	);

	const tabs: NavbarItem[] = [
		useAuthorizedValue(
			{
				link: "server-settings",
				text: intl.formatMessage({ id: "Server Settings", defaultMessage: "Settings" }),
			},
			"ManageSettings",
		),
		useLicensedValue(
			useAuthorizedValue(
				{ link: "machines", text: intl.formatMessage({ id: "Machines", defaultMessage: "Machines" }) },
				"ManageMachines",
			),
			"Core",
			"basic",
			"ff-pack-net-machines",
		),
		useLicensedValue(
			useAuthorizedValue(
				{
					link: "materials",
					text: intl.formatMessage({ id: "Materials", defaultMessage: "Materials" }),
				},
				["ManageCorrugates", "ManageDesigns", "ManageLabelTemplates"],
			),
			"Core",
			"basic",
			"ff-pack-net-materials",
		),
		useLicensedValue(
			useAuthorizedValue(
				{
					link: "print",
					text: intl.formatMessage({ id: "Print", defaultMessage: "Print" }),
				},
				["ManagePrintImages"],
			),
			"Core",
			"basic",
			"ff-pack-net-materials",
		),
		useLicensedValue(
			useAuthorizedValue(
				{
					link: "machine-groups",
					text: intl.formatMessage({ id: "Machine Groups", defaultMessage: "Machine Groups" }),
				},
				"ManageMachineGroups",
			),
			"Core",
			"basic",
			"ff-pack-net-machine-groups",
		),
		useLicensedValue(
			useAuthorizedValue(
				{
					link: "production-groups",
					text: intl.formatMessage({ id: "Production Groups", defaultMessage: "Production Groups" }),
				},
				"ManageProductionGroups",
			),
			"Core",
			"basic",
			"ff-pack-net-production-groups",
		),
		useLicensedValue(
			useAuthorizedValue(
				{
					link: "import-pipeline",
					text: intl.formatMessage({ id: "Import Pipeline", defaultMessage: "Import Pipeline" }),
				},
				"ManageImportPipelines",
			),
			"Core",
			"basic",
			"ff-pack-net-import",
		),
		useLicensedValue(
			useAuthorizedValue(
				{
					link: "machine-service",
					text: intl.formatMessage({ id: "Machine Service", defaultMessage: "Machine Service" }),
				},
				"AccessServiceModePage",
			),
			"Core",
			"basic",
			"ff-pack-net-machines",
		),
		useLicensedValue(
			useAuthorizedValue(
				{
					link: "advanced-workflows",
					text: intl.formatMessage({ id: "Advanced Workflows", defaultMessage: "Advanced Workflows" }),
				},
				["ManageCPGs", "ManageClassifications", "ManagePickZones"],
			),
			"Core",
			"enterprise",
			["ff-pack-net-carton-property-groups", "ff-pack-net-classifications", "ff-pack-net-pick-zones"],
		),
		useAuthorizedValue(
			{ link: "roles", text: intl.formatMessage({ id: "Roles", defaultMessage: "Roles" }) },
			"ManageRoles",
		),
		useAuthorizedValue(
			{ link: "users", text: intl.formatMessage({ id: "Users", defaultMessage: "Users" }) },
			"ManageUsers",
		),
		useLicensedValue(
			useAuthorizedValue(
				{
					link: "event-notifications",
					text: intl.formatMessage({ id: "Event Notifications", defaultMessage: "Event Notifications" }),
				},
				"ManageEventNotifications",
			),
			"Core",
			"professional",
			"ff-pack-net-event-notifications",
		),
		useLicensedValue(
			useAuthorizedValue(
				{
					link: "planned-production",
					text: intl.formatMessage({ id: "Planned Production", defaultMessage: "Planned Production" }),
				},
				"ManagePlannedProduction",
			),
			"Reporting",
			"professional",
			undefined,
			true,
			null,
			true,
		),
		useFeatureFlaggedValue(
			useAuthorizedValue(
				{ link: "secure-print", text: intl.formatMessage({ id: "Secure Print", defaultMessage: "Secure Print" }) },
				"ManageImportPipelines",
			),
			"machines-allow-secure-print",
		),
		environment !== "prod1" && environment !== "legacy-prod1" ? ordersAuditTab : null,
		useAuthorizedValue(
			{
				link: "configuration-audit",
				text: intl.formatMessage({ id: "Configuration Audit", defaultMessage: "Configuration Audit" }),
			},
			"AccessConfigurationAudit",
		),
	].filter((tab): tab is NavbarItem => tab !== null && (tab.link !== "print" || hasNazomiMachine === true));

	return (
		<SiteContent header={<SecondaryNavbar navbarItems={tabs} />}>
			<Outlet />
		</SiteContent>
	);
});

export default ConfigurationNav;
